import { supabase } from '../lib/supabase';
import type {
  PicqerLocation,
  PicqerLocationStockHistoryEntry,
  PicqerWarehouse,
} from '../pages/PicqerAnalyse/types';
import { picqerKeyPool } from './picqerKeyPool';

interface PicqerAnalyseResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  offset?: number;
  limit?: number;
  hasMore?: boolean;
  keySlot?: number;
}

interface PoolInfo {
  poolSize: number;
}

async function invokeOnce<T>(
  action: string,
  payload: Record<string, unknown> = {},
): Promise<PicqerAnalyseResponse<T>> {
  const { data, error } = await supabase.functions.invoke('picqer-analyse', {
    body: { action, ...payload },
  });

  if (error) {
    if (error.context && typeof error.context === 'object' && 'body' in error.context) {
      try {
        const body = error.context.body;
        const parsed = typeof body === 'string' ? JSON.parse(body) : body;
        if (parsed?.error) return { success: false, error: String(parsed.error) };
      } catch {
        // fall through
      }
    }
    return { success: false, error: error.message };
  }

  if (!data || typeof data !== 'object') {
    return { success: false, error: 'Empty response from server' };
  }

  const result = data as PicqerAnalyseResponse<T>;
  if (!result.success) {
    return { success: false, error: result.error ?? 'Unknown error' };
  }

  return result;
}

function isRateLimitError(error?: string): boolean {
  if (!error) return false;
  return error.includes('429') || error.toLowerCase().includes('rate limit');
}

async function fetchPoolSize(): Promise<number> {
  const result = await invokeOnce<PoolInfo>('get-pool-info');
  if (!result.success || !result.data) return 1;
  return result.data.poolSize;
}

let poolInitStarted = false;

export async function ensurePicqerKeyPool(): Promise<number> {
  return picqerKeyPool.ensureInitialized(fetchPoolSize);
}

function startPoolInit(): void {
  if (!poolInitStarted) {
    poolInitStarted = true;
    void ensurePicqerKeyPool();
  }
}

async function invoke<T>(
  action: string,
  payload: Record<string, unknown> = {},
  keySlot?: number,
): Promise<PicqerAnalyseResponse<T>> {
  startPoolInit();
  await picqerKeyPool.ensureInitialized(fetchPoolSize);

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const slot = await picqerKeyPool.acquire(keySlot);
    const result = await invokeOnce<T>(action, { ...payload, keySlot: slot });
    if (result.success || !isRateLimitError(result.error) || attempt === 2) {
      return result;
    }
    await delay(2000 * (attempt + 1));
  }
  return { success: false, error: 'Rate limit exceeded' };
}

export async function getWarehouses(): Promise<PicqerAnalyseResponse<PicqerWarehouse[]>> {
  return invoke<PicqerWarehouse[]>('get-warehouses');
}

export async function getLocationsPage(
  idwarehouse: number,
  offset: number,
  limit = 100,
  keySlot?: number,
): Promise<PicqerAnalyseResponse<PicqerLocation[]>> {
  return invoke<PicqerLocation[]>('get-locations-page', { idwarehouse, offset, limit }, keySlot);
}

export async function getLocationStockHistoryPage(
  idlocation: number,
  options: {
    sincedate?: string | null;
    untildate?: string | null;
    offset?: number;
    limit?: number;
    keySlot?: number;
  } = {},
): Promise<PicqerAnalyseResponse<PicqerLocationStockHistoryEntry[]>> {
  const { keySlot, ...rest } = options;
  return invoke<PicqerLocationStockHistoryEntry[]>('get-location-stock-history-page', {
    idlocation,
    offset: rest.offset ?? 0,
    limit: rest.limit ?? 100,
    ...(rest.sincedate !== undefined ? { sincedate: rest.sincedate } : {}),
    ...(rest.untildate !== undefined ? { untildate: rest.untildate } : {}),
  }, keySlot);
}

export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export { picqerKeyPool };
